import { Fragment, memo, useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import {
  ResearchTypePracticeVM,
  ResearchTypePracticeTableVM,
  BodyResearchTypePractice,
} from 'practice/view-models/research-type-practice';
import { Typography, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { useNavigate } from 'react-router-dom';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import {
  HttpRequestFunction,
  RequestOptionsOverride,
  ResponsePromise,
} from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpBackendError } from 'shell/error/definitions/http-backend-error';
import { HttpResponseList } from 'shell/models/http-response';
import { transformDataset } from 'practice/pages/type-practice/utils';
import { TypePracticeData } from 'shell/models/type-practice';

interface TableTypePracticeProps {
  activeDelete?: boolean;
  activeEdit?: boolean;
  getTypePractice:
    | ((options?: RequestOptionsOverride | undefined) => Promise<Error | HttpBackendError>)
    | HttpRequestFunction<HttpResponseList<TypePracticeData>, BodyResearchTypePractice[]>;
  setDeleteAlertModal: (value: AlertModal<BodyResearchTypePractice>) => void;
  typePratice: Array<BodyResearchTypePractice>;
}

const TableTypePracticeComponent = ({
  activeDelete,
  activeEdit,
  setDeleteAlertModal,
  getTypePractice,
  typePratice,
}: TableTypePracticeProps): React.ReactElement => {
  const formContext = useFormikContext<ResearchTypePracticeVM>();

  const navigate = useNavigate();

  const onChange = useEventCallback(
    (
      key: keyof ResearchTypePracticeTableVM,
      value: ResearchTypePracticeTableVM[keyof ResearchTypePracticeTableVM]
    ) => {
      formContext.setFieldValue('table', {
        ...(formContext.values.table || {}),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: ResearchTypePracticeTableVM) => {
    formContext.setFieldValue('table', {
      ...(formContext.values.table || {}),
      ...values,
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChanges({
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyResearchTypePractice): Promise<BodyResearchTypePractice> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-tipo-pratica/dettaglio/${cell.code}`);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: BodyResearchTypePractice): Promise<BodyResearchTypePractice> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-tipo-pratica/modifica/${cell?.code}`);
      });
    }
  );

  const onTrashPress = useCallback(
    (cell: BodyResearchTypePractice): Promise<BodyResearchTypePractice> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<BodyResearchTypePractice>;
    },
    [setDeleteAlertModal]
  );

  useEffect(() => {
    if (typePratice?.length > 0) {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: typePratice,
      });
    } else {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: typePratice,
      });
    }
  }, [typePratice]);

  const onSort = useCallback(
    (orders) => {
      const newDataset = {
        ...formContext.values,
        filter: {
          ...formContext.values?.filter,
          orders,
        },
      };
      const queryString = transformDataset(newDataset);
      (
        getTypePractice({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypePracticeData>, Array<BodyResearchTypePractice>>
      ).then(() => {
        formContext.setFieldValue('filter', {
          ...{
            ...formContext.values?.filter,
            orders,
          },
        });
      });
    },
    [formContext.setFieldValue, getTypePractice, formContext.values]
  );

  return (
    <Fragment>
      {formContext.values.table?.body ? (
        (formContext.values.table?.body || [])?.length > 0 ? (
          <Table
            ariaLabel="Lista tipi pratiche"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={formContext.values.table?.body || []}
            count={(formContext.values.table?.body || [])?.length}
            head={headCells}
            onSort={onSort}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            orders={formContext.values.filter?.orders}
            isNotLayoutFixed
            activeActions={{
              delete: activeDelete,
              read: true,
              edit: activeEdit,
            }}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

TableTypePracticeComponent.displayName = 'Table Type Practice component';

const TableTypePractice = memo(TableTypePracticeComponent);

export default TableTypePractice;
